import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Form,
    Input,
    InputNumber,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    CheckCircleOutlined,
    CloudSyncOutlined,
    DatabaseOutlined,
    DownloadOutlined,
    LoadingOutlined,
    ReloadOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    RollbackOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ResourceCode } from 'constants/resourceCodes';
import { hasResource } from 'utils/resourceAuth';
import {
    AddressCoordDedupExecutionRequest,
    AddressCoordDedupJobSnapshot,
    AddressCoordDedupJobStatus,
    AddressCoordDedupJobType,
    cancelAddressCoordDedupJob,
    listAddressCoordDedupJobs,
    startAddressCoordDedupJob,
} from './addressCoordDedupApi';

type FormValues = {
    fullImport: boolean;
    zipcode: string;
    importBatchSize: number;
    validationBatchSize: number;
    updateBatchSize: number;
    limit: number;
    maxGoogleCalls: number;
    minGoogleCallIntervalMs: number;
    dataSource: string;
    validationRuleVersion: string;
};

type FlowConfig = {
    type: AddressCoordDedupJobType;
    title: string;
    description: string;
    icon: React.ReactNode;
    accentClass: string;
    successLabel: string;
    danger?: boolean;
};

const FLOW_CONFIGS: FlowConfig[] = [
    {
        type: 'IMPORT',
        title: '导入数据',
        description: '将抽数表数据导入到校验记录表中',
        icon: <DatabaseOutlined />,
        accentClass: 'from-cyan-500 to-sky-600',
        successLabel: '插入成功条数',
    },
    {
        type: 'VALIDATION',
        title: 'Google 校验',
        description: '通过 Google 校验经纬度',
        icon: <CloudSyncOutlined />,
        accentClass: 'from-amber-500 to-orange-600',
        successLabel: 'RESOLVED 条数',
    },
    {
        type: 'UPDATE_ADDRESS_CORE',
        title: '回写 address_core',
        description: '将校验通过的经纬度回写到 address_core',
        icon: <CheckCircleOutlined />,
        accentClass: 'from-emerald-500 to-teal-600',
        successLabel: '回写成功条数',
    },
    {
        type: 'ROLLBACK_ADDRESS_CORE',
        title: '回退 address_core',
        description: '把当前批次数据回退',
        icon: <RollbackOutlined />,
        accentClass: 'from-rose-500 to-red-600',
        successLabel: '回退成功条数',
        danger: true,
    },
];

const DEFAULT_VALUES: FormValues = {
    fullImport: true,
    zipcode: '',
    importBatchSize: 5000,
    validationBatchSize: 200,
    updateBatchSize: 5000,
    limit: 1000,
    maxGoogleCalls: 100,
    minGoogleCallIntervalMs: 300,
    dataSource: 'LOCAL_400W_SIM',
    validationRuleVersion: 'v1',
};

const STATUS_LABELS: Record<AddressCoordDedupJobStatus | 'IDLE', string> = {
    IDLE: '未执行',
    RUNNING: '执行中',
    CANCEL_REQUESTED: '中断中',
    CANCELLED: '已中断',
    SUCCESS: '执行成功',
    FAILED: '执行失败',
};

const STATUS_COLORS: Record<AddressCoordDedupJobStatus | 'IDLE', string> = {
    IDLE: 'default',
    RUNNING: 'processing',
    CANCEL_REQUESTED: 'warning',
    CANCELLED: 'default',
    SUCCESS: 'success',
    FAILED: 'error',
};

const IMPORT_MODE_OPTIONS = [
    { label: '从头全量导入', value: true },
    { label: '增量继续导入', value: false },
];

function formatTime(value?: string | null) {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function formatDuration(seconds?: number | null) {
    if (seconds == null) return '-';
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.ceil((seconds % 3600) / 60)}m`;
}

function isRunning(job?: AddressCoordDedupJobSnapshot) {
    return job?.status === 'RUNNING' || job?.status === 'CANCEL_REQUESTED';
}

function getSummaryItems(job?: AddressCoordDedupJobSnapshot) {
    const summary = job?.summary;
    if (!summary) {
        return [];
    }
    switch (job.type) {
        case 'IMPORT':
            return [
                ['新增', summary.importInsertedCount],
            ];
        case 'VALIDATION':
            return [
                ['校验', summary.validatedCandidateCount],
                ['RESOLVED', summary.resolvedCount],
                ['FAILED', summary.failedCount],
                ['SKIPPED', summary.skippedCount],
                ['真实 Google 调用', summary.googleCallCount],
                ['缓存命中', summary.cacheHitCount],
            ];
        case 'UPDATE_ADDRESS_CORE':
            return [
                ['扫描', summary.updateScannedCount],
                ['成功', summary.updateAppliedCount],
                ['跳过', summary.updateSkippedCount],
                ['失败', summary.updateFailedCount],
            ];
        case 'ROLLBACK_ADDRESS_CORE':
            return [
                ['扫描', summary.rollbackScannedCount],
                ['成功', summary.rollbackAppliedCount],
                ['失败', summary.rollbackFailedCount],
            ];
        default:
            return [];
    }
}

function formatReasonCounts(
    counts?: Record<string, number> | null,
    maxEntries = 5,
    options?: { groupNos?: number[] }
) {
    if (!counts) return '';
    const entries = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return '';

    const visible = entries
        .slice(0, maxEntries)
        .map(([reason, count]) => {
            if (
                reason === 'GROUP_FIRST_TWO_GOOGLE_SAME_COORDINATES' &&
                options?.groupNos?.length
            ) {
                return `${reason}: ${count} (group_no: ${options.groupNos.join(', ')})`;
            }
            return `${reason}: ${count}`;
        })
        .join(' / ');
    const restCount = entries.slice(maxEntries).reduce((total, [, count]) => total + count, 0);
    return restCount > 0 ? `${visible} / 其他: ${restCount}` : visible;
}

function getReasonSummary(job?: AddressCoordDedupJobSnapshot) {
    const summary = job?.summary;
    if (!summary) return '';
    return [
        formatReasonCounts(summary.failureReasonCounts),
        formatReasonCounts(summary.skipReasonCounts, 5, {
            groupNos: summary.groupShortcutSkippedGroupNos,
        }),
        formatReasonCounts(summary.updateFailureReasonCounts),
    ].filter(Boolean).join(' | ');
}

function getFailureReasonSummary(job?: AddressCoordDedupJobSnapshot) {
    const summary = job?.summary;
    if (!summary) return '';

    if (job.type === 'UPDATE_ADDRESS_CORE') {
        return formatReasonCounts(summary.updateFailureReasonCounts);
    }

    return formatReasonCounts(summary.failureReasonCounts);
}

function renderCompactZipcode(value?: string | null) {
    if (!value) {
        return '-';
    }

    return (
        <Typography.Paragraph
            className="!mb-0 max-w-[260px]"
            ellipsis={{
                rows: 1,
                expandable: true,
                symbol: '展开',
                tooltip: value,
            }}
        >
            {value}
        </Typography.Paragraph>
    );
}

function renderCompactReasonText(value?: string | null, maxWidthClass = 'max-w-[300px]') {
    if (!value) {
        return '-';
    }

    return (
        <Typography.Paragraph
            className={`!mb-0 text-xs ${maxWidthClass}`}
            ellipsis={{
                rows: 1,
                expandable: true,
                symbol: '展开',
                tooltip: value,
            }}
        >
            {value}
        </Typography.Paragraph>
    );
}

const AddressCoordDedupControlPage: React.FC = () => {
    const [form] = Form.useForm<FormValues>();
    const [jobs, setJobs] = useState<AddressCoordDedupJobSnapshot[]>([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<AddressCoordDedupJobType | string | null>(null);
    const canViewPage = hasResource([ResourceCode.addressCoordDedup.VIEW]);
    const canStartJob = hasResource([ResourceCode.addressCoordDedup.START]);
    const canCancelJob = hasResource([ResourceCode.addressCoordDedup.CANCEL]);
    const canRollbackAddressCore = hasResource([ResourceCode.addressCoordDedup.ROLLBACK]);

    const refreshJobs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listAddressCoordDedupJobs();
            setJobs(response.jobs);
        } catch (error) {
            message.error(error instanceof Error ? error.message : '查询任务状态失败');
        } finally {
            setLoading(false);
        }
    }, []);

    const latestJobByType = useMemo(() => {
        return jobs.reduce<Record<string, AddressCoordDedupJobSnapshot>>((acc, job) => {
            if (!acc[job.type] || dayjs(job.startedAt).isAfter(dayjs(acc[job.type].startedAt))) {
                acc[job.type] = job;
            }
            return acc;
        }, {});
    }, [jobs]);

    const buildRequest = (values: FormValues): AddressCoordDedupExecutionRequest => {
        const zipcodes = values.zipcode
            ?.split(/[\n,，]+/)
            .map(item => item.trim())
            .filter(Boolean) || [];

        return {
            fullImport: values.fullImport,
            importBatchSize: values.importBatchSize,
            validationBatchSize: values.validationBatchSize,
            updateBatchSize: values.updateBatchSize,
            limit: values.limit,
            maxGoogleCalls: values.maxGoogleCalls,
            minGoogleCallIntervalMs: values.minGoogleCallIntervalMs,
            dataSource: values.dataSource,
            validationRuleVersion: values.validationRuleVersion,
            dbFilter: { field: 'zipcode', values: zipcodes },
        };
    };

    const handleStart = async (type: AddressCoordDedupJobType) => {
        const allowed = type === 'ROLLBACK_ADDRESS_CORE' ? canRollbackAddressCore : canStartJob;
        if (!allowed) {
            message.error(type === 'ROLLBACK_ADDRESS_CORE' ? '当前账号没有回退权限' : '当前账号没有执行权限');
            return;
        }
        try {
            const values = await form.validateFields();
            setActionLoading(type);
            await startAddressCoordDedupJob(type, buildRequest(values));
            message.success('任务已提交，请手动刷新查看状态');
        } catch (error) {
            const fallbackMessage = '启动任务失败';
            const validationMessage = typeof error === 'object' && error !== null && 'errorFields' in error
                ? (error as { errorFields?: Array<{ errors?: string[] }> }).errorFields?.[0]?.errors?.[0]
                : null;
            message.error(error instanceof Error ? error.message : validationMessage || fallbackMessage);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancel = async (jobId: string) => {
        if (!canCancelJob) {
            message.error('当前账号没有中断权限');
            return;
        }
        setActionLoading(jobId);
        try {
            await cancelAddressCoordDedupJob(jobId);
            message.success('已发送中断指令，请手动刷新查看状态');
        } catch (error) {
            message.error(error instanceof Error ? error.message : '中断任务失败');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDownloadSql = (job: AddressCoordDedupJobSnapshot) => {
        if (!job.sqlDownloadUrl) {
            message.warning('当前任务还没有可下载的 SQL 文件');
            return;
        }
        window.open(job.sqlDownloadUrl, '_blank');
    };

    const columns: ColumnsType<AddressCoordDedupJobSnapshot> = [
        {
            title: '流程',
            dataIndex: 'type',
            key: 'type',
            render: (type: AddressCoordDedupJobType) => FLOW_CONFIGS.find(item => item.type === type)?.title || type,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: AddressCoordDedupJobStatus) => (
                <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
            ),
        },
        {
            title: '成功条数',
            dataIndex: 'successCount',
            key: 'successCount',
        },
        {
            title: 'zipcode',
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 280,
            render: renderCompactZipcode,
        },
        {
            title: '批次号',
            dataIndex: 'batchId',
            key: 'batchId',
            render: (value: string | null | undefined, record) => value || record.summary?.batchId || '-',
        },
        {
            title: '原因分布',
            key: 'reasonSummary',
            width: 320,
            render: (_, record) => {
                const reasonSummary = getReasonSummary(record);
                return renderCompactReasonText(reasonSummary);
            },
        },
        {
            title: '开始时间',
            dataIndex: 'startedAt',
            key: 'startedAt',
            render: formatTime,
        },
        {
            title: '结束时间',
            dataIndex: 'finishedAt',
            key: 'finishedAt',
            render: formatTime,
        },
        {
            title: '错误',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            render: (value?: string | null) => value || '-',
        },
        {
            title: 'SQL',
            key: 'sql',
            render: (_, record) => record.sqlDownloadUrl ? (
                <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownloadSql(record)}>
                    下载
                </Button>
            ) : '-',
        },
    ];

    if (!canViewPage) {
        return (
            <Alert
                type="error"
                showIcon
                message="无权限访问地址坐标校准页面"
                description="请联系管理员为当前账号分配 ADDRESS_COORD_DEDUP 资源权限。"
            />
        );
    }

    return (
        <div className="min-h-full bg-slate-50 rounded-2xl p-6">
            <div className="mb-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <Typography.Title level={3} className="!mb-2">地址坐标校准流程控制台</Typography.Title>
                        <Typography.Text type="secondary">
                            分别控制导入、Google 校验、address_core 回写和回退。中断会在当前数据库批次或当前 Google 调用结束后生效。
                        </Typography.Text>
                    </div>
                </div>
            </div>

            {loading && (
                <Alert
                    className="mb-5"
                    type="info"
                    showIcon
                    icon={<LoadingOutlined spin />}
                    message="刷新中"
                    description="正在获取最新任务状态，请稍等。"
                />
            )}

            <Alert
                className="mb-5"
                type="warning"
                showIcon
                message="回写和回退都会修改 address_core"
                description="当前链路按 address_hash + address_core.is_deleted=0 定位 address_core；执行前请确认筛选条件和批次范围。"
            />

            <Alert
                className="mb-5"
                type="info"
                showIcon
                message="执行按钮只负责提交后台任务"
                description="任务提交后接口会立即返回；页面不会自动请求后端，避免长任务期间持续压接口。需要看最新状态时请手动点击“刷新状态”。"
            />

            <Card className="mb-5 shadow-sm" title="执行参数">
                <Form form={form} layout="vertical" initialValues={DEFAULT_VALUES}>
                    <Row gutter={16}>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="fullImport" label="导入模式" rules={[{ required: true }]}>
                                <Select options={IMPORT_MODE_OPTIONS} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="dataSource" label="批次来源" rules={[{ required: true }]}>
                                <Input placeholder="BATCH" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="validationRuleVersion" label="规则版本" rules={[{ required: true }]}>
                                <Input placeholder="v1" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="minGoogleCallIntervalMs" label="Google 间隔(ms)" rules={[{ required: true }]}>
                                <InputNumber min={0} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="importBatchSize" label="导入批大小" rules={[{ required: true }]}>
                                <InputNumber min={1} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="validationBatchSize" label="校验批大小" rules={[{ required: true }]}>
                                <InputNumber min={1} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="updateBatchSize" label="回写/回退批大小" rules={[{ required: true }]}>
                                <InputNumber min={1} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="limit" label="校验本轮上限" rules={[{ required: true }]}>
                                <InputNumber min={1} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Form.Item name="maxGoogleCalls" label="Google 调用上限" rules={[{ required: true }]}>
                                <InputNumber min={0} className="w-full" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16} lg={12}>
                            <Form.Item
                                name="zipcode"
                                label="zipcode"
                                rules={[
                                    { required: true, message: '请输入 zipcode' },
                                    {
                                        validator: (_, value?: string) => {
                                            const zipcodes = value
                                                ?.split(/[\n,，]+/)
                                                .map(item => item.trim())
                                                .filter(Boolean);
                                            if (!zipcodes?.length) {
                                                return Promise.reject(new Error('请输入至少一个 zipcode'));
                                            }
                                            if (zipcodes.length > 200) {
                                                return Promise.reject(new Error('zipcode 最多支持 200 个'));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea rows={1} placeholder="必填，多个 zipcode 用逗号或换行分隔" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <div className="mb-4 flex justify-end">
                <Button
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => void refreshJobs()}
                >
                    刷新状态
                </Button>
            </div>

            <Row gutter={[16, 16]} className={`mb-5 transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}>
                {FLOW_CONFIGS.map(flow => {
                    const latestJob = latestJobByType[flow.type];
                    const status = latestJob?.status || 'IDLE';
                    const running = isRunning(latestJob);
                    const canExecuteFlow = flow.type === 'ROLLBACK_ADDRESS_CORE'
                        ? canRollbackAddressCore
                        : canStartJob;
                    return (
                        <Col xs={24} lg={12} xl={6} key={flow.type}>
                            <Card className="h-full shadow-sm overflow-hidden">
                                <div className={`-m-6 mb-5 p-5 bg-gradient-to-r ${flow.accentClass} text-white`}>
                                    <div className="flex items-center gap-3 text-lg font-semibold">
                                        <span className="text-2xl">{flow.icon}</span>
                                        {flow.title}
                                    </div>
                                    <div className="mt-2 text-white/85 text-sm">{flow.description}</div>
                                </div>
                                <Space direction="vertical" size="middle" className="w-full">
                                    <div className="flex items-center justify-between">
                                        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
                                        <Typography.Text type="secondary" className="text-xs">
                                            {formatTime(latestJob?.startedAt)}
                                        </Typography.Text>
                                    </div>
                                    <Statistic title={flow.successLabel} value={latestJob?.successCount || 0} />
                                    {latestJob?.progress && (
                                        <div>
                                            <Progress
                                                percent={
                                                    latestJob.status === 'SUCCESS'
                                                        ? 100
                                                        : latestJob.progress.percent == null
                                                            ? 0
                                                            : Number(latestJob.progress.percent.toFixed(1))
                                                }
                                                status={
                                                    running
                                                        ? 'active'
                                                        : latestJob.status === 'FAILED'
                                                            ? 'exception'
                                                            : latestJob.status === 'SUCCESS'
                                                                ? 'success'
                                                                : 'normal'
                                                }
                                            />
                                            <div className="text-xs text-slate-500 leading-5">
                                                <div>
                                                    {running
                                                        ? latestJob.progress.message
                                                        : latestJob.status === 'SUCCESS'
                                                            ? '本次执行已完成'
                                                            : latestJob.progress.message}
                                                </div>
                                                <div>
                                                    {flow.type === 'IMPORT' && !running
                                                        ? ''
                                                        : (
                                                            <>
                                                                {running ? '进度' : '实际处理'} {latestJob.progress.current}
                                                                {latestJob.progress.total != null ? ` / ${latestJob.progress.total}` : ''}
                                                                {latestJob.progress.processedPerSecond
                                                                    ? ` · ${latestJob.progress.processedPerSecond.toFixed(1)}/s`
                                                                    : ''}
                                                                {running && latestJob.progress.estimatedRemainingSeconds != null
                                                                    ? ` · 预计剩余 ${formatDuration(latestJob.progress.estimatedRemainingSeconds)}`
                                                                    : ''}
                                                            </>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {latestJob?.summary && (
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="批次">{latestJob.summary.batchId}</Descriptions.Item>
                                            <Descriptions.Item label="明细">
                                                {getSummaryItems(latestJob).map(([label, value]) => `${label}: ${value}`).join(' / ')}
                                            </Descriptions.Item>
                                            {getFailureReasonSummary(latestJob) && (
                                                <Descriptions.Item label="失败原因">
                                                    {renderCompactReasonText(getFailureReasonSummary(latestJob), 'max-w-[520px]')}
                                                </Descriptions.Item>
                                            )}
                                            {formatReasonCounts(latestJob.summary.skipReasonCounts) && (
                                                <Descriptions.Item label="跳过原因">
                                                    {renderCompactReasonText(formatReasonCounts(latestJob.summary.skipReasonCounts), 'max-w-[520px]')}
                                                </Descriptions.Item>
                                            )}
                                            <Descriptions.Item label="耗时">{latestJob.summary.executionDurationMs} ms</Descriptions.Item>
                                        </Descriptions>
                                    )}
                                    {latestJob?.errorMessage && (
                                        <Alert type="error" showIcon message={latestJob.errorMessage} />
                                    )}
                                    <Space wrap className="w-full">
                                        <Button
                                            type={flow.danger ? 'default' : 'primary'}
                                            danger={flow.danger}
                                            icon={<PlayCircleOutlined />}
                                            disabled={running || !canExecuteFlow}
                                            loading={actionLoading === flow.type}
                                            onClick={() => void handleStart(flow.type)}
                                        >
                                            执行
                                        </Button>
                                        <Button
                                            icon={<PauseCircleOutlined />}
                                            disabled={!running || !latestJob || !canCancelJob}
                                            loading={actionLoading === latestJob?.jobId}
                                            onClick={() => latestJob && void handleCancel(latestJob.jobId)}
                                        >
                                            中断执行
                                        </Button>
                                        {latestJob?.sqlDownloadUrl && (
                                            <Button
                                                icon={<DownloadOutlined />}
                                                onClick={() => handleDownloadSql(latestJob)}
                                            >
                                                下载 SQL
                                            </Button>
                                        )}
                                    </Space>
                                </Space>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            <Card
                title="执行历史"
                className={`shadow-sm transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}
            >
                <Table
                    rowKey="jobId"
                    columns={columns}
                    dataSource={jobs}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default AddressCoordDedupControlPage;
